import {
  isRegistryCurrency,
  normalizeRegistryCurrency,
  PLATFORM_FALLBACK_CURRENCY,
} from "../intl/currencyRegistry.js";
import { buildPublicCacheKey } from "../utils/publicCacheKey.js";
import { buildQuery, requestJson } from "./apiClient.js";
import {
  dedupeInFlightRequest,
  readMemoryCache,
  writeMemoryCache,
} from "../utils/publicRequestCache.js";
import { buildFxRatesMeta } from "../intl/fxRatesMeta.js";

const FX_CACHE_KEY = buildPublicCacheKey(["fx", "rates"]);
const FX_CACHE_TTL_MS = 60 * 60 * 1000;

const FX_HISTORY_DEFAULT_BASE = "COP";
const FX_HISTORY_DEFAULT_DAYS = 30;
const FX_HISTORY_MIN_DAYS = 1;
const FX_HISTORY_MAX_DAYS = 365;
const FX_HISTORY_CACHE_TTL_MS = 5 * 60 * 1000;

const FALLBACK_RATES = {
  base: "COP",
  rates: {
    COP: 1,
    USD: 0.00025,
    EUR: 0.00023,
    RUB: 0.022,
    MXN: 0.0045,
  },
  asOf: null,
  source: "fallback",
  stale: false,
};

export async function fetchFxRates() {
  const cached = readMemoryCache(FX_CACHE_KEY, FX_CACHE_TTL_MS);
  if (cached) {
    return cached;
  }

  return dedupeInFlightRequest(FX_CACHE_KEY, async () => {
    try {
      const payload = await requestJson("/api/fx/rates");
      const normalized = {
        base: payload.base || "COP",
        rates: payload.rates || FALLBACK_RATES.rates,
        asOf: payload.asOf || payload.meta?.effectiveDate || null,
        source: payload.source || payload.meta?.source || "unknown",
        stale: Boolean(payload.stale ?? payload.meta?.stale),
        ttlSeconds: payload.ttlSeconds ?? payload.meta?.ttlSeconds ?? null,
        meta: buildFxRatesMeta(payload),
      };
      writeMemoryCache(FX_CACHE_KEY, normalized);
      return normalized;
    } catch {
      return {
        ...FALLBACK_RATES,
        meta: buildFxRatesMeta(FALLBACK_RATES, { isFallback: true }),
      };
    }
  });
}

function createFxHistoryError(message, field) {
  const error = new Error(message);
  error.field = field;
  return error;
}

function validateFxRateHistoryParams(currency, days, options = {}) {
  const base = normalizeRegistryCurrency(options.base || FX_HISTORY_DEFAULT_BASE);
  if (base !== FX_HISTORY_DEFAULT_BASE) {
    throw createFxHistoryError("Only base=COP is supported for FX history.", "base");
  }

  const normalizedCurrency = normalizeRegistryCurrency(currency);
  if (!isRegistryCurrency(normalizedCurrency)) {
    throw createFxHistoryError("currency must be a supported registry currency.", "currency");
  }
  if (normalizedCurrency === PLATFORM_FALLBACK_CURRENCY) {
    throw createFxHistoryError(
      "currency must be a converted target currency; COP history is not supported.",
      "currency"
    );
  }

  let normalizedDays = FX_HISTORY_DEFAULT_DAYS;
  if (days != null && days !== "") {
    const parsed = Number.parseInt(String(days), 10);
    if (!Number.isFinite(parsed) || parsed < FX_HISTORY_MIN_DAYS || parsed > FX_HISTORY_MAX_DAYS) {
      throw createFxHistoryError(
        `days must be an integer between ${FX_HISTORY_MIN_DAYS} and ${FX_HISTORY_MAX_DAYS}.`,
        "days"
      );
    }
    normalizedDays = parsed;
  }

  return { base, currency: normalizedCurrency, days: normalizedDays };
}

function normalizeFxRateHistoryResponse(payload, validated) {
  const points = Array.isArray(payload?.points)
    ? payload.points
        .map((point) => ({
          effectiveDate: String(point.effectiveDate || "").slice(0, 10),
          rate: Number(point.rate),
          fetchedAt: point.fetchedAt || null,
          source: point.source || null,
          snapshotId: point.snapshotId ?? null,
        }))
        .filter(
          (point) =>
            point.effectiveDate && Number.isFinite(point.rate) && point.rate > 0
        )
    : [];

  let latest = null;
  if (payload?.latest != null) {
    const rate = Number(payload.latest.rate);
    if (Number.isFinite(rate) && rate > 0) {
      latest = {
        rate,
        asOf: payload.latest.asOf ? String(payload.latest.asOf).slice(0, 10) : null,
        fetchedAt: payload.latest.fetchedAt || null,
        source: payload.latest.source || null,
        snapshotId: payload.latest.snapshotId ?? null,
      };
    }
  }

  return {
    base: validated.base,
    currency: validated.currency,
    days: validated.days,
    points,
    latest,
  };
}

function buildFxHistoryCacheKey(validated) {
  return buildPublicCacheKey([
    "fx",
    "rates",
    "history",
    validated.base,
    validated.currency,
    String(validated.days),
  ]);
}

export async function fetchFxRateHistory(currency, days = FX_HISTORY_DEFAULT_DAYS, options = {}) {
  const validated = validateFxRateHistoryParams(currency, days, options);
  const cacheKey = buildFxHistoryCacheKey(validated);

  if (!options.skipCache) {
    const cached = readMemoryCache(cacheKey, FX_HISTORY_CACHE_TTL_MS);
    if (cached) {
      return cached;
    }
  }

  const executeFetch = async () => {
    const path = `/api/fx/rates/history${buildQuery({
      base: validated.base,
      currency: validated.currency,
      days: validated.days,
    })}`;

    const payload = await requestJson(path, {
      signal: options.signal,
      skipAuthRedirect: options.skipAuthRedirect,
    });

    const normalized = normalizeFxRateHistoryResponse(payload, validated);
    if (!options.skipCache) {
      writeMemoryCache(cacheKey, normalized);
    }
    return normalized;
  };

  // AbortSignal is per-consumer. Sharing one deduped promise can leave a later
  // caller stuck in loading when an earlier signal aborts the shared fetch.
  if (options.signal) {
    return executeFetch();
  }

  return dedupeInFlightRequest(cacheKey, executeFetch);
}
